import React from 'react'
import {
  Grid,
  Typography,
  Button,
  Menu,
  List,
  ListItem,
  Box,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { head, sum } from '@/utils/lodash'

/**
 * Property header component with image, price, and details
 */
const PropertyHeader = ({
  property,
  onMoreOptionsClick,
  anchorEl,
  onCloseMoreOptions,
}) => {
  const currencyFormater = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
  })

  return (
    <Box style={{ top: '0px', zIndex: 1000, borderBottom: '1px solid #eeeff8' }} sx={[{
      display: "flex",
      alignItems: "center",
      py: "0.75rem",
      px: "1.25rem",
      mb: 0,
      backgroundColor: "#fff",
      padding: "0.75rem 1.25rem",
      marginBottom: 0,
      borderBottom: "0 solid rgba(79, 80, 98, 0.3)",
      "&:first-of-type": { borderRadius: "0.65rem 0.65rem 0 0" },
      "html[data-color-mode=\"dark\"] &": { backgroundColor: "#252525 !important", color: "rgba(255, 255, 255, 0.87)", borderColor: "rgba(255, 255, 255, 0.12) !important" },
    }, { borderRadius: 0, backgroundColor: '#fff', p: '1.5rem' }, {
      position: "sticky",
      "@media (max-width: 767.98px)": { position: "relative" },
    }]}>
      <Grid
        container
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Grid>
          <Box sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: 1.5,
            minWidth: 0,
          }}>
            <Box sx={{
              width: 80,
              height: 60,
              flexShrink: 0,
              alignSelf: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "0.65rem",
              overflow: "hidden",
              bgcolor: "#eeeff8",
              "& img": {
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
              },
            }}>
              <Box
                component="img"
                src={
                  head(property.images)?.url ||
                  '/images/default_image.png'
                }
                alt=""
                loading="lazy"
              />
            </Box>
            <Box sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              flex: "1 1 auto",
              minWidth: 0,
              gap: "2px",
              textAlign: "left",
            }}>
              <Box sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                flexWrap: "wrap",
                columnGap: 1.5,
                rowGap: 0,
                minWidth: 0,
              }}>
                <Typography component="p" sx={{
                  m: 0,
                  p: 0,
                  fontSize: 26,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  color: "#070919",
                }}>
                  {currencyFormater.format(
                    property.acquisition?.analysis?.listPrice,
                  ) || '-'}
                </Typography>
                <Typography component="p" sx={{
                  m: 0,
                  p: 0,
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  lineHeight: 1.3,
                  color: "#4f5062",
                }}>
                  {sum(property.beds) || '-'} bd |{' '}
                  {sum(property.baths) || '-'} ba |{' '}
                  {formatter.format(sum(property.sqft)) || '-'}{' '}
                  sqft | {property.year || '-'} yr
                </Typography>
              </Box>
              <Typography sx={{
                m: 0,
                p: 0,
                fontSize: "0.875rem",
                fontWeight: 400,
                lineHeight: 1.3,
                color: "#4f5062",
              }}>
                {property.address}, {property.city}{' '}
                {property.state} {property.zip}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid>
          <Grid
            container
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid>
              <Button
                variant="contained"
                onClick={onMoreOptionsClick}
                sx={[{ height: "44px", width: "44px", lineHeight: "44px" }, {
                  boxShadow: "0 0.25rem 0.55rem rgba(244, 119, 46, 0.35)",
                  color: "#fff",
                  backgroundColor: "#f4772e",
                  backgroundImage: "none",
                  borderColor: "#f4772e",
                  "&:not(:disabled):hover": { backgroundColor: "#f4772e", color: "#fff" },
                  "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#e35b0c" },
                  "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(244, 119, 46, 0.4), 0 0.0625rem 0.385rem rgba(244, 119, 46, 0.54)" },
                  "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                }]}>
                <FontAwesomeIcon icon={['fas', 'ellipsis-vertical']} color="#fcfcfc" sx={{ fontSize: "1.265rem !important" }} />
              </Button>
              <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={onCloseMoreOptions}
                MenuListProps={{ sx: { p: 0 } }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <Box sx={{ minWidth: "22rem" }}>
                  <List
                    component="div"
                    sx={{
                      "& .MuiListItem-root.MuiListItem-button": { borderRadius: 0 },
                      "& .MuiListItem-root.MuiListItem-button.Mui-selected, & .MuiListItem-root.MuiListItem-button:hover, & .MuiListItemButton-root.Mui-selected, & .MuiListItemButton-root:hover": {
                        color: "#368cff",
                        background: "rgba(54, 140, 255, 0.05)",
                        "&::before": { background: "#368cff" },
                      },
                    }}
                  >
                    <ListItem>
                      <span style={{ color: '#909497' }}>
                        More Options
                      </span>
                    </ListItem>
                    <ListItem
                      button
                      onClick={() =>
                        window.open(
                          `https://www.zillow.com/homes/${property.address?.replace(/\s+/g, '-').toLowerCase()},-${property.state},-${property.zip}_rb/`,
                          '_blank',
                        )
                      }
                    >
                      <Grid container direction="row" spacing={1}>
                        <Grid>
                          <FontAwesomeIcon icon={[ 'fas', 'arrow-up-right-from-square', ]} style={{ marginRight: 2 }} sx={{ fontSize: "1.15rem !important" }} />
                        </Grid>
                        <Grid>
                          <Typography>Open Zillow</Typography>
                        </Grid>
                      </Grid>
                    </ListItem>
                  </List>
                </Box>
              </Menu>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}

export default PropertyHeader
